/**
 * Query marking the CORS request for a vendored engine. The same URL was served
 * no-cors (no `Access-Control-Allow-Origin`) with a one-year `immutable` header,
 * so a browser cache could replay that copy and fail the CORS check. A distinct
 * URL gives the CORS request its own cache entry; the server ignores the query.
 */
export const VENDOR_CORS_QUERY = '?cors=2';

/**
 * Load tag + load guard for one vendored engine global.
 *
 * WHY CORS: the frame has an opaque origin, so `/vendor/…` is cross-origin to it.
 * A classic no-cors script has "muted errors": anything thrown inside the engine
 * (often a bad argument from game code) reaches `window.onerror` as
 * "Script error." with no message, file, line or stack — the console shows it
 * and "Ask AI to fix" has no evidence. `crossorigin="anonymous"` un-mutes them
 * when the server sends `Access-Control-Allow-Origin: *` (the opaque frame sends
 * `Origin: null`, so `*` is the only value that matches).
 *
 * FALLBACK: when the CORS load fails (header not deployed, dev server), the guard
 * `document.write`s the plain no-cors tag. That is parser-inserted and blocking,
 * so the engine is still ready before any game script runs — errors are muted
 * again, never worse than before. Only when both loads fail is the friendly
 * "could not load" error shown.
 */
export function vendorEngineTags(
  src: string,
  globalName: string,
  missingMessage: string,
): { vendorTag: string; guard: string } {
  // `'<scr' + 'ipt'` keeps the literal close tag out of the inline script.
  return {
    vendorTag: `<script crossorigin="anonymous" src="${src}${VENDOR_CORS_QUERY}"></script>`,
    guard: `
<script>
if (!window.${globalName}) {
  document.write('<scr' + 'ipt src="${src}"></scr' + 'ipt>');
}
</script>
<script>
if (!window.${globalName}) {
  console.error(${JSON.stringify(missingMessage)});
}
</script>`,
  };
}
